
import { useEffect, useRef } from 'react';


export function useNavigateOnArrow() {
    const parentRef = useRef<HTMLTableElement>(null);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!parentRef.current) return;
            navigateOnKey(e);
        };

        const currentParent = parentRef.current;
        currentParent?.addEventListener('keydown', handleKeyDown);
        return () => {
            currentParent?.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    return parentRef;
}


function navigateOnKey(e: KeyboardEvent) {
    const direction = getDirection(e);
    if (!direction) return;
    const current = e.target as FocusableElement;

    focusNextTableRow(current, direction);
    e.preventDefault();
}

function getDirection(e: KeyboardEvent): 1 | -1 | null {
    if (e.key === 'ArrowUp') {
        return -1;
    } else if (e.key === 'ArrowDown') {
        return 1;
    } else if (e.key === 'Enter') {
        const target = e.target as HTMLElement;
        const isTextInput = target.tagName === 'INPUT' && (
            (target as HTMLInputElement).type === 'text' || (target as HTMLInputElement).type === 'number'
        );
        if (!isTextInput) return null;
        return e.shiftKey ? -1 : 1;
    } else {
        return null;
    }
}

function focusNextTableRow(current: FocusableElement, direction: 1 | -1) {
    const tbody = current.closest('tbody');
    const currentRow = current.closest('tr');
    const currentCell = current.closest('td');
    if (!tbody || !currentRow || !currentCell) return;

    const rows = Array.from(tbody.children);
    const currentRowIndex = rows.indexOf(currentRow);
    const currentColumnIndex = Array.from(currentRow.children).indexOf(currentCell);

    const nextRowIndex = currentRowIndex + direction;
    if (nextRowIndex < 0 || nextRowIndex >= rows.length) {
        return;
    }

    const cellsInNextRow = Array.from(rows[nextRowIndex].children);
    const candidateCells = [cellsInNextRow[currentColumnIndex], ...cellsInNextRow].filter(Boolean)
    const candidateInputs = candidateCells.flatMap(cell => Array.from(cell.querySelectorAll(focusableQuery))) as FocusableElement[];
    const nextInput = candidateInputs.find(cell => isValidFocusTarget(cell));
    if (nextInput) {
        nextInput.focus();
    } else {
        const closestNextInput = candidateInputs[currentColumnIndex] ?? candidateInputs[0];
        if (closestNextInput) {
            focusNextTableRow(closestNextInput, direction);
        }
    }
}

function isValidFocusTarget(element: Element): boolean {
    const isFocusableType = element instanceof HTMLInputElement
        || element instanceof HTMLTextAreaElement
        || element instanceof HTMLSelectElement
        || element instanceof HTMLButtonElement;
    return isFocusableType && !element.disabled && element.tabIndex !== -1;
}

type FocusableElement = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | HTMLButtonElement;
const focusableQuery = 'input, textarea, select, button';
